import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import { useTheme } from '@mui/material/styles';

import useAuth from '../../hooks/useAuth';
import { ROLE_LABELS } from '../../config/constants';
import gblLogo from '../../images/gbl logo.png';

let NotificationBell = null;
try {
  NotificationBell = require('../notifications/NotificationBell').default;
} catch {
  NotificationBell = null;
}

const SIDEBAR_WIDTH = 72;

const TopBar = ({ onMenuClick }) => {
  const { mongoUser, logout, role } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    try { await logout(); } catch (err) { console.error('Logout failed:', err); }
  };

  const getInitials = () => {
    const name = mongoUser?.name || '';
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        color: '#202124',
        borderBottom: '1px solid #DADCE0',
        zIndex: theme.zIndex.drawer + 1,
        width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
        ml: { md: `${SIDEBAR_WIDTH}px` },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1.5, md: 2.5 },
          height: 56,
          gap: 1,
        }}
      >
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { md: 'none' }, color: '#5F6368', mr: 0.5 }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          component="img"
          src={gblLogo}
          alt="GBL Tree"
          sx={{
            height: 28,
            width: 'auto',
            cursor: 'pointer',
            display: { md: 'none' },
          }}
          onClick={() => navigate('/dashboard')}
        />

        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            color: '#202124',
            display: { xs: 'none', md: 'block' },
          }}
        >
          GBL Tree CRM
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {NotificationBell ? (
          <NotificationBell />
        ) : (
          <IconButton size="small" sx={{ color: '#5F6368' }}>
            <Badge badgeContent={0} color="error">
              <NotificationsOutlined fontSize="small" />
            </Badge>
          </IconButton>
        )}

        <IconButton onClick={handleAvatarClick} sx={{ ml: 0.5, p: 0.25 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: '#1A73E8',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
            src={mongoUser?.avatar || undefined}
            alt={mongoUser?.name || 'User'}
          >
            {getInitials()}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: { minWidth: 220, mt: 1, borderRadius: 2, border: '1px solid #DADCE0' },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#202124' }}>
              {mongoUser?.name || 'User'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#5F6368' }}>
              {mongoUser?.email || ''}
            </Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: '#1A73E8', mt: 0.25, textTransform: 'capitalize' }}>
              {ROLE_LABELS[role] || role}
            </Typography>
          </Box>
          <Divider />
          <MenuItem component="a" href="/profile" sx={{ py: 1, px: 2 }}>
            <ListItemIcon><PersonOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem' }}>Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ py: 1, px: 2, color: '#D93025' }}>
            <ListItemIcon><LogoutOutlined fontSize="small" sx={{ color: '#D93025' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.8125rem' }}>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AppBar>
  );
};

export { SIDEBAR_WIDTH };
export default TopBar;
